<template>
  <div class="wt-asset-info wt2-sg">
    <div
      v-show="!_.isEmpty(assetTitle)"
      class="wt-asset-title wt2-text-m2"
      v-text="assetTitle"
    />

    <div v-if="showCreationDate" class="wt-asset-date wt2-text-s">
      <template v-if="workModel.metadata.creationDate">
        {{ printDateTime(workModel.metadata.creationDate) }}
      </template>
      <template v-else>Date Not Available</template>
      | ID: {{ workModel.metadata.assetUUID }}
      <template v-if="isKlipy">
        |
        <img
          class="-powered-by-klipy"
          src="/resources/img/powered-by-klipy.png"
          alt="Powered by KLIPY"
        />
      </template>
    </div>

    <div v-if="showAssetDescription" class="-section-box">
      <div class="-section-title wt2-text-n2">Description</div>
      <div
        v-if="captionText"
        class="-section-description"
        v-text="captionText"
      />
      <div
        v-if="galleryRestrictions"
        class="-section-description"
        v-text="galleryRestrictions"
      />
    </div>

    <div
      v-if="showRestrictionPanel"
      class="wt-asset-restrictions -section-box"
      ref="restrictions"
      data-original-title
      title
    >
      <span class="wt-asset-restrictions-title wt2-text-n2">
        <span class="wt-asset-restrictions-title -section-title">
          <i
            class="wticon wticon-Restriction_v8"
            :class="restrictedIconClass"
          />
          <span v-text="restrictionsTitle" />
        </span>
      </span>
      <span
        class="wt-aseet-restrictions-content wt2-text-s"
        v-text="restrictionsText"
      />
    </div>

    <ul v-if="showMoreInfo" class="wt2-inline-list -more-info">
      <li v-if="canBackToVideo">
        <a class="wt2-color-link" @click.prevent="$_onBackToVideo"
          >Show original video</a
        >
      </li>
    </ul>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's Fragment Details Vue (bottom bar)
 */
import app from 'app';
import { getOriginalVideoFromFragment } from '../../store/assetEditorApi';
import { debounce } from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

export default {
  computed: {
    ...mapGetters('assetEditor', ['canBackToVideo', 'isInMyGallery']),
    ...mapState('assetEditor', ['workModel']),

    isKlipy() {
      const providerId = this.workModel?.metadata?.providerId;
      return (
        typeof providerId === 'string' && providerId.toLowerCase() === 'klipy'
      );
    },

    showMoreInfo() {
      return !app.assetType.isWALLPAPER(this.workModel);
    },

    showAssetDescription() {
      return (
        !app.assetType.isGIF(this.workModel) &&
        !app.assetType.isWEBPAGE(this.workModel) &&
        !app.assetType.isWALLPAPER(this.workModel) &&
        this.assetDescription
      );
    },

    captionText() {
      return this.workModel.metadata.caption ?? '';
    },

    galleryRestrictions() {
      return this.isInMyGallery ? this.restrictionsText : '';
    },

    assetDescription() {
      return [this.captionText, this.galleryRestrictions]
        .filter((text) => text && text.trim())
        .join('\n');
    },

    restrictedIconClass() {
      return {
        '-restricted':
          this.isRestricted(this.workModel) && !this.isDynamic(this.workModel),
      };
    },

    restrictionsTitle() {
      if (this.isFairUse(this.workModel)) {
        return '3rd party content:';
      } else {
        return 'Restrictions';
      }
    },

    restrictionsText() {
      return this.workModel._restrictions;
    },

    isCommercial() {
      return app.assetType.isCommercial(this.workModel);
    },

    showCreationDate() {
      return !app.assetType.isWALLPAPER(this.workModel);
    },

    showRestrictionPanel() {
      return (
        !this.isInMyGallery &&
        (this.isRestricted(this.workModel) ||
          this.isEditorial(this.workModel) ||
          this.isFairUse(this.workModel) ||
          this.isCommercial)
      );
    },

    assetTitle() {
      if (app.assetType.isFACEBOOK(this.workModel)) {
        return this.workModel.metadata.socialUserName;
      } else if (app.assetType.isINSTAGRAM(this.workModel)) {
        const name = this.workModel.metadata.socialUserName;
        const nickname = this.workModel.metadata.socialUserScreenName;
        return `${name} @ ${nickname}`;
      } else if (app.assetType.isWALLPAPER(this.workModel)) {
        switch (this.workModel.wallpaperMetaData.type) {
          case 'GRADIENT':
            return app.culture.get('wallpaper.title') + ': Gradient';
          case 'SOLID': {
            const bgColour = app.computeBgFromColorData(
              this.workModel.wallpaperMetaData.wallpaperData
            );
            const hexColour = tinycolor(bgColour.background)
              .toHexString()
              .toUpperCase();
            return app.culture.get('wallpaper.title') + `: ${hexColour}`;
          }
        }
      } else {
        return this.workModel.metadata.title;
      }
      return [];
    },
  },

  methods: {
    ...mapActions('assetEditor', ['track', 'NAVIGATE_TO_ASSET']),
    ...mapMutations('assetEditor', ['setOpenedFrom']),

    printDateTime: function (dateLong) {
      return dateLong ? app.moment(dateLong).format('MM/DD/YYYY hh:mma') : '';
    },

    $_onBackToVideo: debounce(
      function () {
        this.track({
          e: 'clickedOriginalVideo',
          params: { Source: 'Asset Editor' },
        });

        this.setOpenedFrom('original-video');
        const asset = getOriginalVideoFromFragment(this.workModel);
        this.NAVIGATE_TO_ASSET({ asset, suppressIan: true });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.wt-asset-info {
  margin-top: 20px;

  .-section-box {
    margin-top: 20px;
    .-section-title {
      margin-bottom: 10px;
    }
  }

  .wt-asset-title {
    line-height: 20px;
    font-size: 16px;
    margin: 0 0 5px 0;
  }

  ul.-more-info {
    margin-top: 15px;
    margin-bottom: 30px;
  }

  .wt-asset-description {
  }

  .wt-asset-description,
  .wt-asset-restrictions {
    line-height: 17px;
    .webpagemetaasset & {
      word-break: break-all;
    }
  }

  .wt-asset-date {
    font-size: 12px;
    margin-bottom: 14px;
    display: flex;
    align-items: center;

    .-powered-by-klipy {
      width: auto;
      height: 14px;
      margin-left: 5px;
      pointer-events: none;
    }
  }

  .wt-asset-restrictions {
    .wt-asset-restrictions-title {
      display: flex;
      align-items: center;

      i {
        background-color: #4c4c4c;
        color: white;
        border-radius: 50%;
        font-size: 16px;
        margin-right: 7px;

        &.-restricted {
          background-color: #ff3166;
        }
      }
    }
  }
}
</style>
